import React from "react";
import { AddButton, IconButton } from "psims/react/components/button";
import useAustralianControllingCorporations from "./use-australian-controlling-corporations";
import { AustralianControllingCorporation, FieldValidation } from "../types";
import styled from "styled-components";
import { ACCValidationField, ACCValidationResult, validateAustralianControllingCorporation } from "./aus-control-corps-validations";
import { getACCInlineMessage } from "./aus-control-corps-inline-messages";

interface Props {
  ctrl: ReturnType<typeof useAustralianControllingCorporations>;
  isDisabled:boolean;
  msoCommentAnnualActivities: Array<{ commentCategoryType?: number; reporterComments?: string }>;  // ← add
  onCommentChange: (comment: string) => void; 
  reportingYear:number;
}

// ─── Styled components ────────────────────────────────────────────────────────

const InfoBox = styled.div`
  background: #f5f7fa;
  border: 1px solid #d6dde6;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
  color: #333;
  p { margin: 0; font-size: 14px; line-height: 1.5; }
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  user-select: none;
`;

const CheckboxInput = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #2684ff;
`;

const CardWrapper = styled.div`
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 16px;
  background: white;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  textarea {
    border: solid 1px silver !important;
    padding: 4px 8px;
    font-size: 14px;
    border: 1px solid #bbb;
    border-radius: 3px;
    background: #fff;
    box-sizing: border-box;
    font-family: inherit;
    outline: none;
    transition: border-color 0.15s;
  }

  .cell-input--error {
    border-color: #d0021b !important;
    background-color: #fff8f8;
  }

  .cell-input--error:focus {
    border-color: #d0021b !important;
    box-shadow: 0 0 0 2px rgba(208, 2, 27, 0.2);
  }
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 220px 1fr;
  align-items: center;
  margin-bottom: 10px;
  gap: 12px;
`;

const Label = styled.label<{ required?: boolean }>`
  font-weight: bold;
  font-size: 14px;
  color: #333;

  ${({ required }) => required && `
    &::after {
      content: ' *';
      color: #d0021b;
    }
  `}
`;

const CellInput = styled.input`
  width: 100%;
  padding: 4px 8px;
  font-size: 14px;
  height: 30px;
  border: 1px solid #bbb;
  border-radius: 3px;
  background: #fff;
  box-sizing: border-box;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s;

  &:focus {
    border-color: #2684ff;
    box-shadow: 0 0 0 2px rgba(38, 132, 255, 0.2);
  }

  &:disabled {
    background: #f5f5f5;
    color: #999;
    cursor: not-allowed;
  }
`;

const CellTextArea = styled.textarea`
  width: 100%;
  padding: 4px 8px;
  font-size: 14px;
  height: 30px;
  border: 1px solid #bbb;
  border-radius: 3px;
  background: #fff;
  box-sizing: border-box;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s;

  &:focus {
    border-color: #2684ff;
    box-shadow: 0 0 0 2px rgba(38, 132, 255, 0.2);
  }

  &:disabled {
    background: #f5f5f5;
    color: #999;
    cursor: not-allowed;
  }
`;

const Actions = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
`;
const ErrorText = styled.span`
  display: block;
  font-size: 12px;
  color: #d0021b;
  margin-top: 3px;
`;
// ─── Card ─────────────────────────────────────────────────────────────────────

interface CardProps {
  row: AustralianControllingCorporation;
  ctrl: ReturnType<typeof useAustralianControllingCorporations>;
  rowValidation: Partial<Record<ACCValidationField, FieldValidation<ACCValidationResult>>>;
  isDisabled: boolean;
}

const CorporationCard = ({ row, ctrl,rowValidation,isDisabled  }: CardProps) => {
  const blur = (field: keyof AustralianControllingCorporation) =>
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      ctrl.update({ ...row, [field]: e.target.value || undefined });
 
  const id = row._key;
  const err = (field: ACCValidationField) => !!rowValidation[field];

  return (
    <CardWrapper>
      <FieldRow>
        <Label htmlFor={`${id}-newCorpName`}>New corporation name</Label>
        <CellInput
          id={`${id}-newCorpName`}
          disabled={isDisabled}
          defaultValue={row.newCorporationName || ''}
          aria-label="New corporation name"
          className={err('newCorporationName') ? 'cell-input--error' : undefined}
          onBlur={blur('newCorporationName')}
        />
      </FieldRow>

      <FieldRow>
        <Label htmlFor={`${id}-abn`} >ABN</Label>
        <CellInput
          id={`${id}-abn`}
          disabled={isDisabled}
          defaultValue={row.abn || ''}
          aria-label="ABN"
          className={err('abn') ? 'cell-input--error' : undefined}
          onBlur={blur('abn')}
        />
      </FieldRow>

      <FieldRow>
        <Label htmlFor={`${id}-acn`} >ACN</Label>
        <CellInput
          id={`${id}-acn`}
          disabled={isDisabled}
          defaultValue={row.acn || ''}
          aria-label="ACN"
          className={err('acn') ? 'cell-input--error' : undefined}
          onBlur={blur('acn')}
        />
      </FieldRow>

      <FieldRow>
        <Label htmlFor={`${id}-address`} >Address</Label>
        <CellInput
          id={`${id}-address`}
          disabled={isDisabled}
          defaultValue={row.address || ''}
          aria-label="Address"
          className={err('address') ? 'cell-input--error' : undefined}
          onBlur={blur('address')}
        />
      </FieldRow>

      <FieldRow>
        <Label htmlFor={`${id}-contact`} >Contact information</Label>
        <CellInput
          id={`${id}-contact`}
          disabled={isDisabled}
          defaultValue={row.contactInformation || ''}
          aria-label="Contact information"
          className={err('contactInformation') ? 'cell-input--error' : undefined}
          onBlur={blur('contactInformation')}
        />
      </FieldRow>

      <Actions style={{ display: 'none' }}>
        <IconButton
          icon="trash"
          onClick={() => ctrl.remove(row)}
          color="dark-grey"
          label="Delete corporation"
        />
      </Actions>
    </CardWrapper>
  );
};

// ─── Editor ───────────────────────────────────────────────────────────────────

const AustralianControllingCorporationsEditor = ({ ctrl, isDisabled,msoCommentAnnualActivities,onCommentChange, reportingYear  }: Props) => {
  const previousYear = reportingYear;
  // The single record is always rows[0] if it exists.
  // hasChangedInPreviousYear drives visibility of the form.
  const singleRow = ctrl.rows[0]?.data;
  const hasChanged = singleRow?.hasChangedInPreviousYear === true;

  const initialComment = msoCommentAnnualActivities
    ?.find(c => c.commentCategoryType === 11)
    ?.reporterComments ?? '';

  const [comment, setComment] = React.useState(initialComment);

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComment(e.target.value);
    onCommentChange(e.target.value);
  }, [onCommentChange]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (checked) {
      if (!singleRow) {
        ctrl.addWithFlag();
      } else {
        ctrl.update({ ...singleRow, hasChangedInPreviousYear: true });
      }
    } else {
      if (singleRow) {
        ctrl.update({ ...singleRow, hasChangedInPreviousYear: false });
      }
    }
  };

  return (
    <div>
      <h2>Australian Controlling Corporations</h2>
      <InfoBox>
        <p>
          This section only needs to be completed if the Australian controlling
          corporation has changed during {previousYear}.<br />
        </p>
      </InfoBox>

      <CheckboxRow>
        <CheckboxInput
          type="checkbox"
          id="hasChangedInPreviousYear"
          checked={hasChanged}
          disabled={isDisabled}
          onChange={handleCheckboxChange}
        />
        <CheckboxLabel htmlFor="hasChangedInPreviousYear">
          Yes, the Australian controlling corporation changed during {previousYear}
        </CheckboxLabel>
      </CheckboxRow>

      {hasChanged && singleRow && (
        <CorporationCard
          row={singleRow}
          ctrl={ctrl}
          isDisabled={isDisabled}
          rowValidation={ctrl.rows.find(r => r.data._key === singleRow._key)?.validations ?? {}}
        />
      )}

      <div style={{ display: 'none' }}>
        <AddButton onClick={ctrl.add} label="">Add corporation</AddButton>
      </div>

      {/* Comments — bound to msoCommentAnnualActivities commentCategoryType: 11 */}
      <div style={{ marginTop: 24 }}>
        <label
          htmlFor="acc_comment"
          style={{ display: 'block', fontWeight: 'bold', marginBottom: 8 }}
        >
          Comments
        </label>
        <textarea
          id="acc_comment"
          disabled={isDisabled}
          value={comment}
          onChange={handleChange}
          maxLength={4000}
          rows={4}
          style={{ width: '100%', padding: '8px', fontSize: '14px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }}
        />
        <div style={{ textAlign: 'right', fontSize: '13px', color: '#666', marginTop: 4 }}>
          {comment.length} of 4000 characters used
        </div>
      </div>
    </div>
  );
};

export default AustralianControllingCorporationsEditor;